import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/useAuthStore';
import { logger } from '../utils/logger';

export type InventoryType = 'tracked' | 'unlimited';

export interface PackageProduct {
  id: string;
  name: string;
  price: number;
  stock_quantity: number | null;
  inventory_type: InventoryType;
  image_url?: string;
}

export interface PackageItem {
  id?: string;
  package_id?: string;
  product_id: string;
  quantity: number;
  unit_cost_override?: number | null;
  product?: PackageProduct;
}

export interface Package {
  id: string;
  empresa_id: string;
  name: string;
  description?: string | null;
  price?: number | null;
  image_url?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  items?: PackageItem[];
  // Catalog parity with products
  mostrar_en_catalogo?: boolean;
  disponible_online?: boolean;
  destacado?: boolean;
  tags?: string[];
  tiempo_preparacion?: string;
  ubicacion?: string;
  orden_catalogo?: number;
  mostrar_precio?: boolean;
  mostrar_stock?: boolean;
  imagenes?: string[];
}

export interface PackageBookableData {
  is_bookable: boolean;
  requires_time_slot: boolean;
  custom_duration_minutes: number | null;
  fulfillment_options: string[];
}

export interface PackageAvailability {
  package_id: string;
  empresa_id: string;
  name: string;
  price?: number | null;
  is_active: boolean;
  available_qty: number;
  has_low_stock: boolean;
}

export interface PackageFormData {
  name: string;
  description: string;
  price: string;
  image_url: string;
  is_active: boolean;
  items: PackageItem[];
  // Catalog
  mostrar_en_catalogo: boolean;
  disponible_online: boolean;
  destacado: boolean;
  tags: string[];
  tiempo_preparacion: string;
  ubicacion: string;
  orden_catalogo: number;
  mostrar_precio: boolean;
  mostrar_stock: boolean;
  imagenes: string[];
  // Scheduling (bookable)
  is_bookable: boolean;
  requires_time_slot: boolean;
  custom_duration_minutes: number | null;
  fulfillment_options: string[];
}

export function usePackages() {
  const currentEmpresa = useAuthStore((s) => s.currentEmpresa);
  const empresaId = currentEmpresa?.id ?? null;

  const [packages, setPackages] = useState<Package[]>([]);
  const [availability, setAvailability] = useState<Record<string, PackageAvailability>>({});
  const [products, setProducts] = useState<PackageProduct[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPackages = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('packages')
        .select(
          `
          *,
          items:package_items (
            id,
            product_id,
            quantity,
            unit_cost_override,
            product:productos (
              id, nombre, precio, stock, inventory_type, imagen, activo
            )
          )
        `
        )
        .eq('empresa_id', empresaId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      const normalized = (data ?? []).map((p: any) => {
        const tagsRaw = p.tags;
        const tagsArray = Array.isArray(tagsRaw)
          ? tagsRaw.filter((t: unknown): t is string => typeof t === 'string')
          : typeof tagsRaw === 'string'
            ? tagsRaw.split(',').map((t: string) => t.trim()).filter(Boolean)
            : [];
        return {
        ...p,
        mostrar_en_catalogo: p.mostrar_en_catalogo === true,
        disponible_online: p.disponible_online === true,
        destacado: p.destacado === true,
        tags: tagsArray,
        tiempo_preparacion: p.tiempo_preparacion || '',
        ubicacion: p.ubicacion || '',
        orden_catalogo: p.orden_catalogo ?? 0,
        mostrar_precio: p.mostrar_precio === true,
        mostrar_stock: p.mostrar_stock === true,
        imagenes: Array.isArray(p.imagenes) ? p.imagenes : (p.image_url ? [p.image_url] : []),
        items: (p.items ?? []).map((i: any) => ({
          ...i,
          product: i.product
            ? {
                id: i.product.id,
                name: i.product.nombre,
                price: Number(i.product.precio) || 0,
                stock_quantity: i.product.stock != null ? Number(i.product.stock) : null,
                inventory_type: (i.product.inventory_type || 'tracked') as InventoryType,
                image_url: i.product.imagen,
              }
            : undefined,
        })),
      };
      });
      setPackages(normalized);
    } catch (err: any) {
      logger.error('Error loading packages', err);
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  const fetchAvailability = useCallback(async () => {
    if (!empresaId) return;
    const { data, error } = await supabase
      .from('package_availability')
      .select('*')
      .eq('empresa_id', empresaId);
    if (!error && data) {
      const map: Record<string, PackageAvailability> = {};
      data.forEach((row: any) => {
        map[row.package_id] = row;
      });
      setAvailability(map);
    }
  }, [empresaId]);

  const fetchProducts = useCallback(async () => {
    if (!empresaId) return;
    const { data, error } = await supabase
      .from('productos')
      .select('id, nombre, precio, stock, inventory_type, imagen')
      .eq('empresa_id', empresaId)
      .or('estado.eq.ACTIVO,estado.is.null')
      .order('nombre');
    if (!error && data) {
      setProducts(
        data.map((p: any) => ({
          id: p.id,
          name: p.nombre,
          price: Number(p.precio) || 0,
          stock_quantity: p.stock != null ? Number(p.stock) : null,
          inventory_type: (p.inventory_type || 'tracked') as InventoryType,
          image_url: p.imagen,
        }))
      );
    }
  }, [empresaId]);

  useEffect(() => {
    fetchPackages();
    fetchAvailability();
    fetchProducts();
  }, [fetchPackages, fetchAvailability, fetchProducts]);

  const createPackage = async (form: PackageFormData): Promise<Package | null> => {
    if (!empresaId) return null;
    try {
      const tagsArray = Array.isArray(form.tags) ? form.tags.filter((t): t is string => typeof t === 'string') : [];
      const catalogPayload = {
        mostrar_en_catalogo: form.mostrar_en_catalogo,
        disponible_online: form.disponible_online,
        destacado: form.destacado,
        tags: tagsArray,
        tiempo_preparacion: form.tiempo_preparacion || null,
        ubicacion: form.ubicacion || null,
        orden_catalogo: form.orden_catalogo ?? 0,
        mostrar_precio: form.mostrar_precio,
        mostrar_stock: form.mostrar_stock,
        imagenes: Array.isArray(form.imagenes) ? form.imagenes : [],
      };
      const fullPayload = {
        empresa_id: empresaId,
        name: form.name.trim(),
        description: form.description.trim() || null,
        price: form.price ? parseFloat(form.price) : null,
        image_url: form.image_url || null,
        is_active: form.is_active,
        ...catalogPayload,
      };
      const { data: pkg, error: pkgErr } = await supabase
        .from('packages')
        .insert(fullPayload)
        .select()
        .single();
      if (pkgErr) throw pkgErr;

      if (form.items.length > 0) {
        const { error: itemsErr } = await supabase.from('package_items').insert(
          form.items.map((item) => ({
            package_id: pkg.id,
            product_id: item.product_id,
            quantity: item.quantity,
            unit_cost_override: item.unit_cost_override ?? null,
          }))
        );
        if (itemsErr) throw itemsErr;
      }
      if (form.is_bookable) {
        await supabase.from('bookable_items').upsert({
          empresa_id: empresaId,
          item_type: 'package',
          item_id: pkg.id,
          is_bookable: true,
          requires_time_slot: form.requires_time_slot !== false,
          custom_duration_minutes: form.custom_duration_minutes,
          fulfillment_options: form.fulfillment_options?.length ? form.fulfillment_options : ['pickup'],
        }, { onConflict: 'empresa_id,item_type,item_id' });
      }
      await fetchPackages();
      await fetchAvailability();
      return pkg;
    } catch (err: any) {
      logger.error('Error creating package', err);
      return null;
    }
  };

  const updatePackage = async (id: string, form: PackageFormData): Promise<boolean> => {
    if (!empresaId) return false;
    try {
      const tagsArray = Array.isArray(form.tags) ? form.tags.filter((t): t is string => typeof t === 'string') : [];
      const catalogPayload = {
        mostrar_en_catalogo: form.mostrar_en_catalogo,
        disponible_online: form.disponible_online,
        destacado: form.destacado,
        tags: tagsArray,
        tiempo_preparacion: form.tiempo_preparacion || null,
        ubicacion: form.ubicacion || null,
        orden_catalogo: form.orden_catalogo ?? 0,
        mostrar_precio: form.mostrar_precio,
        mostrar_stock: form.mostrar_stock,
        imagenes: Array.isArray(form.imagenes) ? form.imagenes : [],
      };
      const fullPayload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price: form.price ? parseFloat(form.price) : null,
        image_url: form.image_url || null,
        is_active: form.is_active,
        ...catalogPayload,
      };
      const { error: pkgErr } = await supabase.from('packages').update(fullPayload).eq('id', id);
      if (pkgErr) throw pkgErr;

      await supabase.from('package_items').delete().eq('package_id', id);
      if (form.items.length > 0) {
        const { error: itemsErr } = await supabase.from('package_items').insert(
          form.items.map((item) => ({
            package_id: id,
            product_id: item.product_id,
            quantity: item.quantity,
            unit_cost_override: item.unit_cost_override ?? null,
          }))
        );
        if (itemsErr) throw itemsErr;
      }
      if (form.is_bookable) {
        await supabase.from('bookable_items').upsert({
          empresa_id: empresaId,
          item_type: 'package',
          item_id: id,
          is_bookable: true,
          requires_time_slot: form.requires_time_slot !== false,
          custom_duration_minutes: form.custom_duration_minutes,
          fulfillment_options: form.fulfillment_options?.length ? form.fulfillment_options : ['pickup'],
        }, { onConflict: 'empresa_id,item_type,item_id' });
      } else {
        await supabase.from('bookable_items').delete().eq('empresa_id', empresaId).eq('item_type', 'package').eq('item_id', id);
      }
      await fetchPackages();
      await fetchAvailability();
      return true;
    } catch (err: any) {
      logger.error('Error updating package', err);
      return false;
    }
  };

  const sellPackage = async (
    packageId: string,
    qtySold: number,
    _costOverrides?: Record<string, number>
  ): Promise<{ success: boolean; warnings: string[] }> => {
    if (!empresaId) return { success: false, warnings: [] };
    try {
      const { data, error } = await supabase.rpc('sell_package', {
        p_package_id: packageId,
        p_qty_sold: qtySold,
        p_empresa_id: empresaId,
      });
      if (error) throw error;
      const warnings: string[] = data?.warnings ?? [];
      if (warnings.length > 0) {
        warnings.forEach((w) => logger.warn('Package sale warning', w));
      }
      await fetchPackages();
      await fetchAvailability();
      return { success: true, warnings };
    } catch (err: any) {
      logger.error('Error selling package', err);
      return { success: false, warnings: [] };
    }
  };

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from('packages').update({ is_active: !current }).eq('id', id);
    await fetchPackages();
    await fetchAvailability();
  };

  const deletePackage = async (id: string) => {
    try {
      await supabase.from('bookable_items').delete().eq('item_type', 'package').eq('item_id', id);
      const { error } = await supabase.from('packages').delete().eq('id', id);
      if (error) throw error;
      await fetchPackages();
      await fetchAvailability();
    } catch (err: any) {
      logger.error('Error deleting package', err);
    }
  };

  const getAvailableQty = (pkg: Package): number => {
    if (!pkg.items || pkg.items.length === 0) return 0;
    const tracked = pkg.items.filter((i) => i.product?.inventory_type === 'tracked');
    if (tracked.length === 0) return Infinity;
    return Math.min(
      ...tracked.map((i) => Math.floor((i.product?.stock_quantity ?? 0) / i.quantity))
    );
  };

  const loadBookableForPackage = useCallback(async (packageId: string): Promise<PackageBookableData | null> => {
    if (!empresaId) return null;
    const { data, error } = await supabase
      .from('bookable_items')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('item_type', 'package')
      .eq('item_id', packageId)
      .maybeSingle();
    if (error || !data) return null;
    return {
      is_bookable: data.is_bookable || false,
      requires_time_slot: data.requires_time_slot !== false,
      custom_duration_minutes: data.custom_duration_minutes ?? null,
      fulfillment_options: data.fulfillment_options || ['pickup'],
    };
  }, [empresaId]);

  const uploadPackageImages = useCallback(async (packageId: string, files: File[]): Promise<string[]> => {
    if (!empresaId || files.length === 0) return [];
    const uploadedUrls: string[] = [];
    for (const file of files) {
      try {
        const fileExt = file.name.split('.').pop() || 'jpg';
        const fileName = `packages/${empresaId}/${packageId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const { error } = await supabase.storage
          .from('productos')
          .upload(fileName, file, { cacheControl: '3600', upsert: false });
        if (error) {
          logger.warn('Package image upload failed', { fileName, error: error.message });
          continue;
        }
        const { data: { publicUrl } } = supabase.storage.from('productos').getPublicUrl(fileName);
        uploadedUrls.push(publicUrl);
      } catch (err) {
        logger.warn('Package image upload error', err);
      }
    }
    return uploadedUrls;
  }, [empresaId]);

  return {
    packages,
    availability,
    products,
    loading,
    createPackage,
    updatePackage,
    sellPackage,
    toggleActive,
    deletePackage,
    getAvailableQty,
    loadBookableForPackage,
    uploadPackageImages,
    refresh: fetchPackages,
  };
}
